import React, { useEffect, useState } from "react";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, Pencil, X, Mail } from "lucide-react";
import ImageUpload from "@/admin/ImageUpload";
import { useConfirm } from "@/lib/confirm";

const ROLES = ["super_admin", "manager", "instructor"];
const ROLE_COLORS = {
  super_admin: "bg-red-600/10 text-red-500 border-red-600/30",
  manager: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  instructor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const confirm = useConfirm();

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/admin-users"); setUsers(data); }
    catch (e) { if (e.response?.status === 403) setForbidden(true); }
    finally { setLoading(false); }
  };

  const save = async (u) => {
    try {
      if (u.id) {
        const { id, ...body } = u; delete body.password_hash; delete body.created_at; delete body.updated_at;
        await api.put(`/admin/admin-users/${id}`, body);
      } else {
        await api.post("/admin/admin-users", u);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPw = async (id, pw) => {
    try { await api.post(`/admin/admin-users/${id}/reset-password`, { new_password: pw });
      toast.success("Password reset"); setResetting(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    const ok = await confirm({ title: "Delete this admin?", description: "They will lose access immediately.", danger: true, confirmLabel: "Delete admin" });
    if (!ok) return;
    try { await api.delete(`/admin/admin-users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendResetLink = async (id, email) => {
    const ok = await confirm({ title: "Email reset link?", description: `A one-time password reset link will be emailed to ${email}. Link expires in 24 hours.`, confirmLabel: "Send link" });
    if (!ok) return;
    try { await api.post(`/admin/admin-users/${id}/send-reset-link`);
      toast.success("Reset link emailed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-4 sm:p-8 text-zinc-500">Loading…</div>;
  if (forbidden) return <div className="p-4 sm:p-8"><h1 className="font-display text-4xl uppercase">Access denied</h1><p className="text-zinc-400 mt-4">Only super admins can manage admin users. Your role is <strong>{me?.role}</strong>.</p></div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">System</div>
          <h1 className="font-display text-5xl uppercase">Admins &amp; Team</h1>
          <p className="text-zinc-400 mt-2 text-sm">Manage who can log in and who appears on the public <a href="/team" target="_blank" rel="noreferrer" className="text-red-500 underline">Meet the Team</a> page. Toggle "Show on team" per person to control visibility.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            const name = window.prompt("Type instructor's full name (e.g. 'Shubham Rane') — we'll create the account instantly.");
            if (!name || !name.trim()) return;
            try {
              const { data } = await api.post("/admin/admin-users/quick-add", { name: name.trim() });
              await confirm({ title: "Instructor added ✓", description: `Email: ${data.email}\nPassword: ${data.password}\n\nCopy these now — the password won't be shown again. Ask them to change it after first login.`, confirmLabel: "Got it", cancelLabel: null });
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }} className="btn-ghost flex items-center gap-2" data-testid="quick-add-instructor"><Plus size={14}/>Quick add instructor</button>
          <button onClick={() => setEditing({ role: "manager", active: true })} className="btn-red flex items-center gap-2" data-testid="add-admin-user"><Plus size={14}/>Add admin</button>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-xs">
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">On team</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-semibold">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${ROLE_COLORS[u.role] || "border-white/10"}`}>{u.role}</span></td>
                <td className="px-4 py-3">{u.show_on_team ? <span className="text-emerald-500 text-xs">✓ {u.team_category || ""}</span> : <span className="text-zinc-600 text-xs">—</span>}</td>
                <td className="px-4 py-3">{u.active !== false ? <span className="text-emerald-500">●</span> : <span className="text-zinc-600">○</span>}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{u.created_at?.slice(0,10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => sendResetLink(u.id, u.email)} className="text-zinc-400 hover:text-emerald-500 p-1" title="Email reset link" data-testid={`send-reset-link-${u.id}`}><Mail size={14}/></button>
                  <button onClick={() => setResetting(u)} className="text-zinc-400 hover:text-amber-400 p-1 ml-1" title="Set password now" data-testid={`reset-pw-${u.id}`}><KeyRound size={14}/></button>
                  <button onClick={() => setEditing(u)} className="text-zinc-400 hover:text-white p-1 ml-1" data-testid={`edit-admin-${u.id}`}><Pencil size={14}/></button>
                  {u.id !== me?.id && <button onClick={() => del(u.id)} className="text-zinc-400 hover:text-red-500 p-1 ml-1" data-testid={`delete-admin-${u.id}`}><Trash2 size={14}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditAdminModal item={editing} onClose={() => setEditing(null)} onSave={save}/>}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onReset={resetPw}/>}
    </div>
  );
}

function EditAdminModal({ item, onClose, onSave }) {
  const [f, setF] = useState({ email: "", name: "", password: "", role: "manager", active: true, show_on_team: false, ...item });
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-8 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between mb-6"><h2 className="font-display text-3xl uppercase">{item.id ? "Edit admin" : "New admin"}</h2><button onClick={onClose}><X size={20}/></button></div>
        <input placeholder="Email" value={f.email || ""} onChange={e => setF({...f, email: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" data-testid="admin-user-email"/>
        <input placeholder="Full name" value={f.name || ""} onChange={e => setF({...f, name: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" data-testid="admin-user-name"/>
        {!item.id && <input type="password" placeholder="Password (min 8 chars)" value={f.password || ""} onChange={e => setF({...f, password: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" data-testid="admin-user-password"/>}
        <select value={f.role} onChange={e => setF({...f, role: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" data-testid="admin-user-role">
          {ROLES.map(r => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
        </select>
        {f.role === "instructor" && (
          <input placeholder="Instructor name (must match Session.instructor field)" value={f.instructor_name || ""}
            onChange={e => setF({...f, instructor_name: e.target.value})}
            className="w-full bg-black border border-white/10 px-3 py-2 mb-3 text-sm" data-testid="admin-user-instructor-name"/>
        )}
        <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={f.active !== false} onChange={e => setF({...f, active: e.target.checked})}/>Active</label>

        <div className="border-t border-white/10 pt-4 mt-2">
          <div className="eyebrow mb-3">Public team page</div>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={!!f.show_on_team} onChange={e => setF({...f, show_on_team: e.target.checked})} data-testid="show-on-team"/>
            Show this person on the public <span className="font-mono text-xs">/team</span> page
          </label>
          {f.show_on_team && (
            <div className="space-y-3">
              <select value={f.team_category || ""} onChange={e => setF({...f, team_category: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="team-category">
                <option value="">— Section on /team —</option>
                <option value="management">Management</option>
                <option value="instructor">Instructor</option>
                <option value="competition">Competition Team</option>
              </select>
              <input placeholder='Public role title (e.g. "Founder & Head Coach")' value={f.team_role_title || ""} onChange={e => setF({...f, team_role_title: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="team-role-title"/>
              <ImageUpload value={f.photo || ""} onChange={v => setF({...f, photo: v})} label="Photo (upload or paste URL)" testId="team-photo-upload"/>
              <input placeholder='Styles (e.g. "Hip Hop, Bollywood, Locking")' value={f.styles || ""} onChange={e => setF({...f, styles: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="team-styles"/>
              <textarea rows={3} placeholder="Short bio for the team page" value={f.bio || ""} onChange={e => setF({...f, bio: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="team-bio"/>
              <input type="number" placeholder="Order (lower = higher up)" value={f.team_order ?? ""} onChange={e => setF({...f, team_order: e.target.value === "" ? null : Number(e.target.value)})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="team-order"/>
            </div>
          )}
        </div>

        <button onClick={() => onSave(f)} className="btn-red w-full mt-6" data-testid="save-admin-user">Save</button>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onReset }) {
  const [pw, setPw] = useState("");
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between mb-6"><h2 className="font-display text-3xl uppercase">Reset password</h2><button onClick={onClose}><X size={20}/></button></div>
        <p className="text-zinc-400 text-sm mb-4">Master reset for <strong>{user.email}</strong>. They will need to log in with this new password.</p>
        <input type="password" placeholder="New password (min 8 chars)" value={pw} onChange={e => setPw(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 mb-4 text-sm" data-testid="new-password-input"/>
        <button onClick={() => onReset(user.id, pw)} disabled={pw.length < 8} className="btn-red w-full disabled:opacity-50" data-testid="submit-reset-password">Reset password</button>
      </div>
    </div>
  );
}
